import React, { useState } from "react";
import styled from "styled-components";
import { colors2, DropdownSingle, TextInput, UI_BODY_SM_DF } from "oolib";
import { ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
} from "./BaseIntegrationInterface";

// ============================================
// APP STORE ICON
// ============================================
// Apple App Store mark — blue-gradient rounded square with the official white
// App Store "A" glyph (three rounded strokes). Glyph path sourced from Simple
// Icons (CC0); its own 24x24 viewBox is nested and centred inside the square.

export const AppStoreReviewsIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient
        id="appStoreReviewsGrad"
        x1="16"
        y1="0"
        x2="16"
        y2="32"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#17C9FB" />
        <stop offset="1" stopColor="#1A74E8" />
      </linearGradient>
    </defs>
    <rect width="32" height="32" rx="7" fill="url(#appStoreReviewsGrad)" />
    <svg x="6" y="6" width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#FFFFFF"
        d="M8.8086 14.9194l6.1107-11.0368c.0837-.1513.1682-.302.2437-.4584.0685-.142.1267-.2854.1646-.4403.0803-.3259.0588-.6656-.066-.9767-.1238-.3095-.3417-.5678-.6201-.7355a1.4175 1.4175 0 0 0-.921-.1924c-.3207.043-.6135.1935-.8443.4288-.1094.1118-.1996.2361-.2832.369-.092.1463-.175.2979-.259.4492l-.3864.6979-.3865-.6979c-.0837-.1515-.1667-.303-.2587-.4492-.0837-.1329-.1739-.2572-.2835-.369-.2305-.2353-.5233-.3857-.844-.429a1.4181 1.4181 0 0 0-.921.1926c-.2784.1677-.4964.426-.6203.7355-.1246.311-.1461.6508-.066.9767.038.155.0962.2984.1648.4403.0753.1564.1598.307.2437.4584l1.248 2.2543-4.8625 8.7825H2.0295c-.1676 0-.3351-.0007-.5026.0092-.1522.009-.3004.0284-.448.0714-.3108.0906-.5822.2798-.7783.548-.195.2665-.3006.5929-.3006.9279 0 .3352.1057.6612.3006.9277.196.2683.4675.4575.7782.548.1477.043.296.0623.4481.0715.1675.01.335.009.5026.009h13.0974c.0171-.0357.059-.1294.1-.2697.415-1.4151-.6156-2.843-2.0347-2.843zM3.113 18.5418l-.7922 1.5008c-.0818.1553-.1644.31-.2384.4705-.067.1458-.124.293-.1611.452-.0785.3346-.0576.6834.0645 1.0029.1212.3175.3346.583.607.7549.2727.172.5891.2416.9013.1975.3139-.044.6005-.1986.8263-.4402.1072-.1148.1954-.2424.2772-.3787.0902-.1503.1714-.3059.2535-.4612L6 19.4636c-.0896-.149-.9473-1.4704-2.887-.9218m20.5861-3.0056a1.4707 1.4707 0 0 0-.779-.5407c-.1476-.0425-.2961-.0616-.4483-.0705-.1678-.0099-.3352-.0091-.503-.0091H18.648l-4.3891-7.817c-.6655.7005-.9632 1.485-1.0773 2.1976-.1655 1.0333.0367 2.0934.546 3.0004l5.2741 9.3933c.084.1494.167.299.2591.4435.0837.131.1739.2537.2836.364.231.2323.5238.3809.8449.4232.3192.0424.643-.0244.9217-.1899.2784-.1653.4968-.4204.621-.7257.1246-.3072.146-.6425.0658-.9641-.0381-.1529-.0962-.2945-.165-.4346-.0753-.1543-.1598-.303-.2438-.4524l-1.216-2.1662h1.596c.1677 0 .3351.0009.5029-.009.1522-.009.3007-.028.4483-.0705a1.4707 1.4707 0 0 0 .779-.5407A1.5386 1.5386 0 0 0 24 16.452a1.539 1.539 0 0 0-.3009-.9158Z"
      />
    </svg>
  </svg>
);

const FieldRow = styled.div`
  margin-top: 16px;
`;

const NoteText = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

// ============================================
// INTERFACE
// ============================================

// Accepts a full App Store URL or a raw numeric ID and returns the numeric
// App Store ID ("" if none found). App Store links look like
// https://apps.apple.com/us/app/spotify-music/id324684580?uo=4 — the ID is the
// run of digits right after "/id".
export const extractAppStoreId = (input) => {
  const s = (input || "").trim();
  if (!s) return "";
  if (/^\d{6,12}$/.test(s)) return s; // already a bare ID
  const m = s.match(/id(\d{6,12})/i); // pulled out of a URL
  return m ? m[1] : "";
};

// Apple tasks are slow on DataForSEO (~1.3s/review); 200 is the most that
// reliably finishes inside the 2-minute poll cap, so that's the ceiling.
export const REVIEW_COUNT_OPTIONS = [
  { display: "50 most recent", value: 50 },
  { display: "100 most recent", value: 100 },
  { display: "200 most recent", value: 200 },
];

export const AppStoreReviewsInterface = ({ onSubmit, isLoading }) => {
  const [appInput, setAppInput] = useState("");
  const [reviewCount, setReviewCount] = useState(REVIEW_COUNT_OPTIONS[1]); // 100

  // The user may paste the App Store link OR the bare numeric ID; we extract
  // the ID and send only that to the adapter (its contract is unchanged).
  const appId = extractAppStoreId(appInput);
  const appIdValid = /^\d{6,12}$/.test(appId);
  const canSubmit = appIdValid && !isLoading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      adapterOptions: {
        appId,
        maxRecords: reviewCount?.value,
      },
    });
  };

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from App Store Reviews</InterfaceHeader>
      <InterfaceDescription>
        Pull the most recent user reviews for an iOS app. Each review becomes
        one row with rating, content, author, and any developer reply.
      </InterfaceDescription>

      <FieldRow>
        <TextInput
          label="App Store link or ID"
          placeholder="e.g. https://apps.apple.com/app/spotify-music/id324684580"
          value={appInput}
          onChange={(k, v) => setAppInput(v)}
          readOnly={isLoading}
        />
        {appInput.trim() && !appIdValid && (
          <NoteText style={{ color: "#c0392b" }}>
            Could not find an App Store ID. Paste the App Store link, or the
            numeric ID itself.
          </NoteText>
        )}
        {!appInput.trim() && (
          <NoteText>
            Paste the full App Store link (e.g.{" "}
            <strong>apps.apple.com/app/spotify-music/id324684580</strong>)
          </NoteText>
        )}
      </FieldRow>

      <FieldRow>
        <DropdownSingle
          label="Maximum reviews to fetch"
          placeholder="Select a limit..."
          options={REVIEW_COUNT_OPTIONS}
          value={reviewCount}
          onChange={(k, v) => setReviewCount(v)}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </FieldRow>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit}
          isLoading={isLoading}
        >
          Fetch Reviews
        </ButtonPrimary>
      </InterfaceActions>

      <HelpText>
        Powered by DataForSEO. We handle the credentials on our side. Larger
        imports take longer time to complete — keep this tab open while it runs.
      </HelpText>
    </InterfaceContainer>
  );
};

export const appStoreReviewsIntegrationMeta = {
  id: "appStoreReviews",
  name: "App Store Reviews",
  description: "Pull recent user reviews for an iOS app from the Apple App Store",
};

export default AppStoreReviewsInterface;
